import { Box, BoxProps, Flex, Text } from "@chakra-ui/react"
import React, { Children, ComponentPropsWithoutRef } from "react"

type SwitchInputProps = Pick<
  ComponentPropsWithoutRef<"input">,
  "id" | "checked" | "onChange" | "name"
>

type SwitchProps = Omit<BoxProps, keyof SwitchInputProps> &
  SwitchInputProps & {
    isDisabled?: boolean
    isReadOnly?: boolean
    label?: "left" | "right"
  }

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      alignItems = "center",
      id,
      checked,
      onChange,
      name,
      isDisabled,
      isReadOnly,
      label = "left",
      children,
      ...props
    },
    ref
  ) => {
    const inputProps = {
      id,
      checked,
      onChange,
      name,
      disabled: isDisabled,
      readOnly: isReadOnly,
    }

    const flexDirection = label === "left" ? "row-reverse" : "row"

    const hasChildren = Children.count(children) > 0

    return (
      <Flex
        as="label"
        alignItems={alignItems}
        gap={2}
        flexDirection={flexDirection}
        {...props}
      >
        <Box
          pos="relative"
          w="2.125rem"
          h={5}
          p={0.5}
          m={0.5}
          rounded="full"
          flex="0 0 auto"
          bg="ds.background.neutral.bold.resting"
          role="switch"
          sx={{
            "&::before": {
              content: '""',
              pos: "absolute",
              inset: 0,
              rounded: "full",
              opacity: 0,
              outline: "0px solid transparent",
              outlineColor: "ds.border.focused",
              pointerEvents: "none",
            },

            "&:has(:focus-visible)": {
              "&::before": {
                opacity: 0.5,
                outlineWidth: "4px",
              },
            },

            "&:hover": {
              bg: "ds.background.neutral.bold.hovered",
            },

            path: {
              strokeDasharray: "1",
              strokeDashoffset: "1",
            },

            ":has(:checked)": {
              bg: "ds.background.brand.bold.resting",

              "&:not(:has(:disabled, [readonly])):hover": {
                bg: "ds.background.brand.bold.hovered",
              },

              "> span": {
                transform: "translateX(14px)",
              },

              path: {
                strokeDashoffset: "0",
                transition:
                  "stroke-dashoffset 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              },
            },

            ":has(:disabled, [readonly])": {
              bg: "ds.background.disabled",

              span: {
                color: "ds.icon.disabled",
              },
            },
          }}
        >
          <Box
            as="input"
            type="checkbox"
            ref={ref}
            pos="absolute"
            inset={0}
            w="full"
            h="full"
            opacity={0}
            cursor="pointer"
            {...inputProps}
          />
          <Box
            as="span"
            display="block"
            rounded="full"
            w={4}
            h={4}
            bg="ds.surface.raised.resting"
            color="ds.icon.selected"
            shadow="ds.raised"
            pointerEvents="none"
            transition="transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4.5 8l2.5 2.5l5.5-5.5"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                pathLength={1}
              />
            </svg>
          </Box>
        </Box>
        {hasChildren && (
          <Text
            flex="1 1 auto"
            textStyle="ds.paragraph.primary"
            color={isDisabled ? "ds.text.disabled" : "ds.text.default"}
            my={0.5}
          >
            {children}
          </Text>
        )}
      </Flex>
    )
  }
)
